// Défi mode — runs a fixed batch of cards then shows a recap modal.
// Extracts startDefi / endDefi from legacy/app.js. Legacy keeps the
// event-handler wiring for the recap modal's own buttons (restart /
// back / backdrop) since those cross module boundaries.
//
// The defi state object lives on the god-object for now (slice 17
// dissolves that); this module only reads/writes it through deps.

import { GAME_MODES } from "../config/modes";
import { t } from "../i18n";
import { byId, hide, show } from "./dom";
import { renderErrorRows } from "./error-row";

// Internal défi state shape. Error tracking moved to session.errors —
// défi state is now just pacing / identity info, and endDefi pulls
// the recap's error list straight from the session.
export type DefiStateLike = {
  total: number;
  seenIds?: Set<string>;
  done: boolean;
  startedAt?: number;
};

type DefiError = { cardId: string; prompt: string; expected: string };

export type DefiDeps = {
  getCards: () => unknown[];
  getDefi: () => DefiStateLike | null;
  setDefi: (d: DefiStateLike | null) => void;
  getSession: () => {
    attempts: number;
    correct: number;
    errors: DefiError[];
    hintedAttempts?: number;
    hintedCorrect?: number;
  };
  setSession: (s: {
    attempts: number;
    correct: number;
    errors: DefiError[];
    hintedAttempts?: number;
    hintedCorrect?: number;
  }) => void;
  nextCard: () => void;
  /** Drop the persisted-défi blob from localStorage. Called by
   *  startDefi (fresh start overrides any resume candidate) and by
   *  endDefi (a finished défi has nothing to resume). Optional to keep
   *  this module decoupled from the persistence layer in tests. */
  clearPersistedDefi?: () => void;
};

export type DefiController = {
  startDefi: () => void;
  /** Show the défi end modal. Pass `interrupted: true` when the user
   *  cut the défi short via the "Terminer" button — the title switches
   *  from "Résultat du défi" to "Défi interrompu" so the recap is
   *  honest about what it's summarizing. */
  endDefi: (opts?: { interrupted?: boolean }) => void;
};

export function createDefiController(deps: DefiDeps): DefiController {
  function startDefi(): void {
    // Stamp the start time so a persisted+resumed défi keeps its
    // original clock. Cleared by the caller's clearPersistedDefi()
    // (defi.ts startDefi doesn't persist itself — the session layer
    // owns persistence and will save on the first answer).
    deps.setDefi({
      total: GAME_MODES.defi.count,
      done: false,
      seenIds: new Set<string>(),
      startedAt: Date.now(),
    });
    deps.setSession({ attempts: 0, correct: 0, errors: [] });
    deps.clearPersistedDefi?.();
    if (deps.getCards().length) deps.nextCard();
  }

  function endDefi(opts?: { interrupted?: boolean }): void {
    const d = deps.getDefi();
    if (!d) return;
    const interrupted = !!opts?.interrupted;
    // Only mark the défi as done / drop its persistence when it
    // actually completed. An interrupted recap is a *preview*: the
    // user can still tap Reprendre to continue, so the in-progress
    // state must survive the modal being open.
    if (!interrupted) {
      d.done = true;
      deps.clearPersistedDefi?.();
    }
    const sess = deps.getSession();
    const correct = sess.correct;
    const total = sess.attempts;
    const errors = sess.errors;
    const hintedAttempts = sess.hintedAttempts ?? 0;
    const hintedCorrect = sess.hintedCorrect ?? 0;
    const unhintedAttempts = total - hintedAttempts;
    const unhintedCorrect = correct - hintedCorrect;

    const title = byId("defiEndTitle");
    if (title) {
      title.textContent = t(
        interrupted ? "defi_end_title_interrupted" : "defi_end_title",
      ) as string;
    }
    // Three-line recap: progression ▶ N/total, unhinted success ✓ X/Y,
    // hinted success ◐ X/Y. Each success line is hidden when that
    // category has zero attempts (no "0/0" clutter). Icons match the
    // in-session stats pill so the vocabulary is consistent.
    const defiTotal = d.total;
    const pct = (n: number, d: number): string =>
      d > 0 ? ` · ${Math.round((n / d) * 100)}%` : "";
    const score = byId("defiEndScore");
    if (score) score.textContent = `▶ ${total} / ${defiTotal}`;
    const unhintedLine = byId("defiEndUnhintedLine");
    if (unhintedLine) {
      if (unhintedAttempts > 0) {
        unhintedLine.textContent = `✓ ${unhintedCorrect} / ${unhintedAttempts}${pct(unhintedCorrect, unhintedAttempts)}`;
        unhintedLine.classList.remove("hidden");
      } else {
        unhintedLine.classList.add("hidden");
      }
    }
    const hintedScore = byId("defiEndHintedScore");
    if (hintedScore) {
      if (hintedAttempts > 0) {
        hintedScore.textContent = `◐ ${hintedCorrect} / ${hintedAttempts}${pct(hintedCorrect, hintedAttempts)}`;
        hintedScore.classList.remove("hidden");
      } else {
        hintedScore.classList.add("hidden");
      }
    }

    const perfectEl = byId("defiEndPerfect");
    if (perfectEl) {
      // Only surface the "no errors" note when the user actually
      // answered something — a défi interrupted at 0/50 has
      // errors.length === 0 trivially and shouldn't get praised.
      if (total > 0 && errors.length === 0) {
        perfectEl.textContent = t("defi_perfect") as string;
        perfectEl.classList.remove("hidden");
      } else {
        perfectEl.classList.add("hidden");
      }
    }

    const errSection = byId("defiEndErrorsSection");
    const errList = byId("defiEndErrorsList");
    if (errors.length > 0) {
      const errTitle = byId("defiEndErrorsTitle");
      if (errTitle) errTitle.textContent = t("defi_errors_title") as string;
      if (errList) errList.innerHTML = renderErrorRows(errors);
      errSection?.classList.remove("hidden");
    } else {
      errSection?.classList.add("hidden");
    }

    const restartBtn = byId("defiEndRestartBtn");
    if (restartBtn) restartBtn.textContent = t("defi_restart") as string;
    const backBtn = byId("defiEndBackBtn");
    if (backBtn) backBtn.textContent = t("defi_back") as string;
    const resumeBtn = byId("defiEndResumeBtn");
    if (resumeBtn) resumeBtn.textContent = t("defi_resume_btn") as string;
    // Swap between "Mode libre" (natural completion — no défi left
    // to resume) and "Reprendre" (interruption — state is preserved).
    // Nouveau défi stays visible in both cases as the "restart" exit.
    if (interrupted) {
      backBtn?.classList.add("hidden");
      resumeBtn?.classList.remove("hidden");
    } else {
      backBtn?.classList.remove("hidden");
      resumeBtn?.classList.add("hidden");
    }
    show(byId("defiEndModal"));
  }

  return { startDefi, endDefi };
}
